/**
 * The offline OUTBOX. A message typed with no connection used to be discarded in
 * silence: `send` got a `network_error`, set an error signal, and the text was
 * gone — no queue, no retry, nothing to resend. On a subway, in a lift, on flaky
 * mobile data, that is the user losing what they wrote.
 *
 * This queues those turns durably (so they survive the reload that a killed tab
 * causes) and hands them back for replay when the connection returns. Pure +
 * storage-injected (DIP), so the whole queue is unit-tested with no browser.
 *
 * Deliberately NOT an offline history cache: reading old conversations offline is
 * a separate, larger concern. This solves the DATA-LOSS half, which is the half
 * that cannot be undone by simply reopening the app.
 */

/** Where the queue is persisted between sessions. */
export const OUTBOX_STORAGE_KEY = '4f:outbox';

/**
 * Hard cap on queued turns. An unbounded queue can fill `localStorage` and start
 * throwing on every write; dropping the OLDEST keeps what the user most recently
 * wrote, which is what they will look for.
 */
export const OUTBOX_LIMIT = 50;

/** One turn waiting to be sent. */
export interface OutboxEntry {
  /** Stable client id — the de-duplication + removal key. */
  readonly id: string;
  readonly conversationId: string;
  readonly text: string;
  /** Epoch ms, for ordering and for showing how long it has waited. */
  readonly queuedAt: number;
}

/** The persistence seam (DIP over `localStorage`). */
export interface OutboxStorage {
  read(): string | null;
  write(value: string): void;
}

/** The default `localStorage`-backed storage; a silent no-op when unavailable. */
export function browserOutboxStorage(): OutboxStorage {
  return {
    read() {
      try {
        return globalThis.localStorage?.getItem(OUTBOX_STORAGE_KEY) ?? null;
      } catch {
        return null;
      }
    },
    write(value) {
      try {
        globalThis.localStorage?.setItem(OUTBOX_STORAGE_KEY, value);
      } catch {
        // Storage full or disabled — the queue degrades to this session only.
      }
    },
  };
}

/**
 * Parse a persisted queue. Corrupt or partially-written JSON yields an EMPTY
 * queue rather than throwing: a poisoned outbox must never brick the app shell
 * on boot.
 */
export function parseOutbox(raw: string | null): readonly OutboxEntry[] {
  if (!raw) {
    return [];
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isEntry);
}

/** Structural guard — drops any row that is not a well-formed entry. */
function isEntry(value: unknown): value is OutboxEntry {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === 'string' &&
    row.id.length > 0 &&
    typeof row.conversationId === 'string' &&
    row.conversationId.length > 0 &&
    typeof row.text === 'string' &&
    row.text.length > 0 &&
    typeof row.queuedAt === 'number'
  );
}

export interface Outbox {
  /** Everything still waiting, oldest first. */
  list(): readonly OutboxEntry[];
  /** Queued turns for one conversation, oldest first. */
  listFor(conversationId: string): readonly OutboxEntry[];
  /** Queue a turn; returns the stored entry. */
  enqueue(conversationId: string, text: string): OutboxEntry;
  /** Drop a delivered (or abandoned) turn. Idempotent. */
  remove(id: string): void;
  /** Drop everything (sign-out). */
  clear(): void;
}

/** Injected id + clock sources so queued entries are deterministic under test. */
export interface OutboxDeps {
  readonly storage?: OutboxStorage;
  readonly now?: () => number;
  readonly newId?: () => string;
}

export function createOutbox(deps: OutboxDeps = {}): Outbox {
  const storage = deps.storage ?? browserOutboxStorage();
  const now = deps.now ?? (() => Date.now());
  const newId = deps.newId ?? (() => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  /**
   * STORAGE is the queue, not this object. Every read re-parses it, and every
   * write is computed from a fresh read.
   *
   * Caching it in memory instead means two live instances over the same storage
   * — a second tab, or the installed PWA beside the browser — diverge the moment
   * either one delivers something: the other still holds the entry, replays it,
   * and the user is billed twice for a message they sent once. The wire carries
   * no idempotency key, so the edge cannot catch that for us.
   */
  const load = (): readonly OutboxEntry[] => parseOutbox(storage.read());

  const persist = (next: readonly OutboxEntry[]): void => {
    storage.write(JSON.stringify(next));
  };

  return {
    list: () => load(),
    listFor: (conversationId) => load().filter((e) => e.conversationId === conversationId),
    enqueue(conversationId, text) {
      const entry: OutboxEntry = { id: newId(), conversationId, text, queuedAt: now() };
      // Drop the OLDEST past the cap — the newest writing is what the user wants back.
      persist(load().concat(entry).slice(-OUTBOX_LIMIT));
      return entry;
    },
    remove(id) {
      persist(load().filter((entry) => entry.id !== id));
    },
    clear() {
      persist([]);
    },
  };
}
